function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import PropTypes from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
//Limitless imports
import ToolTip from "../../tooltip/Tooltip";
import { useTranslation } from "@limitless/common/hooks/translation/TranslationContext";
const useStyles = makeStyles(theme => {
  const labelColor = theme.palette.input ? theme.palette.input.label.color : theme.palette.primary.dark;
  return {
    container: {
      display: "flex"
    },
    input: {
      "&:-webkit-autofill": {
        boxShadow: "0 0 0 30px white inset",
        color: theme.palette.primary.main
      },
      "@media screen and (max-width: 450px)": {
        fontSize: 16
      }
    },
    iconButton: {
      zIndex: 1,
      width: 23,
      height: 23,
      "&:hover": {
        backgroundColor: "inherit"
      }
    },
    multilineRoot: {
      padding: "6px 0 3px"
    },
    inputMultiline: {
      overflow: "auto"
    },
    adornmentMultiline: {
      alignItems: "flex-start",
      marginBottom: 10
    },
    "@media screen and (min-width: 450px)": {
      container: {
        marginBottom: 10
      }
    },
    fixedHeightError: {
      height: 24
    },
    formLabel: {
      color: labelColor,
      "&.Mui-error": {
        color: labelColor
      },
      "&.Mui-focused": {
        color: labelColor
      },
      textAlign: props => props.isRtl ? "right" : "left",
      width: "130%",
      display: "block"
    },
    helperTextLeftSpacing: {
      marginLeft: props => props.isRtl ? "auto" : "0",
      marginRight: props => props.isRtl ? "0" : "auto"
    }
  };
});
const InputField = ({
  value,
  onChangeHandle,
  label,
  icon,
  loading,
  errorMessage,
  toolTipTitle,
  multiline,
  rows,
  rootStyles,
  type,
  input,
  noGutterBottom,
  id,
  autoComplete,
  largeHelperMessage,
  inputAdornmentClass
}) => {
  const isRtl = document.documentElement.getAttribute("dir") === "rtl"; // Detect RTL/LTR
  const classes = useStyles({
    isRtl
  });
  const {
    t
  } = useTranslation();
  const handleChange = event => {
    const val = event.target.value;
    onChangeHandle(val);
  };
  const large = rows > 3;
  return /*#__PURE__*/React.createElement(FormControl, {
    className: noGutterBottom ? classes.noGutterContainer : classes.container,
    error: errorMessage ? true : false
  }, /*#__PURE__*/React.createElement(InputLabel, {
    variant: "standard",
    classes: {
      root: classes.formLabel
    }
  }, t(label)), /*#__PURE__*/React.createElement(Input, _extends({
    id: id ? id : label,
    value: value,
    endAdornment: !large ? /*#__PURE__*/React.createElement(ToolTip, {
      title: toolTipTitle
    }, /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      classes: multiline ? {
        root: `${classes.adornmentMultiline} ${inputAdornmentClass}`
      } : {}
    }, /*#__PURE__*/React.createElement(IconButton, {
      className: classes.iconButton,
      "aria-label": "input-icon-button",
      component: "div",
      tabIndex: -1,
      disableRipple: true,
      size: "large"
    }, icon))) : null,
    onChange: handleChange,
    disabled: loading,
    multiline: multiline,
    rows: rows,
    style: rootStyles,
    classes: {
      input: classes.input,
      inputMultiline: classes.inputMultiline,
      multiline: classes.multilineRoot
    },
    type: type,
    autoComplete: autoComplete
  }, input, {
    inputProps: {
      "aria-label": label
    }
  })), /*#__PURE__*/React.createElement(FormHelperText, {
    component: "div",
    className: `${largeHelperMessage && classes.fixedHeightError}`,
    classes: {
      root: classes.helperTextLeftSpacing
    }
  }, t(errorMessage)));
};
InputField.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
  loading: PropTypes.bool,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  errorMessage: PropTypes.string,
  toolTipTitle: PropTypes.string.isRequired,
  onChangeHandle: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  rootStyles: PropTypes.object,
  type: PropTypes.string,
  input: PropTypes.object,
  noGutterBottom: PropTypes.bool,
  id: PropTypes.string,
  autoComplete: PropTypes.string,
  largeHelperMessage: PropTypes.bool,
  inputAdornmentClass: PropTypes.string
};
InputField.defaultProps = {
  noGutterBottom: false,
  autoComplete: "off",
  largeHelperMessage: false,
  inputAdornmentClass: ""
};
export default InputField;